import {useEffect, useRef, useState} from "react";
import {useApi} from "./useApi";
import {mergeActivity} from "../utils/mergeRemote";
import {DOB_RESOLVED_EVENT, readStoredDob} from "../../myCase/dobSession";

// Counters that increased since the DOB-replace baseline (usually {} after
// useStorage clears). Used so a late fetch merges post-DOB ticks without
// resurrecting the previous household member's activity.
const activitySinceBaseline = (current, baseline) => {
  const cur = current && typeof current === "object" ? current : {};
  const base = baseline && typeof baseline === "object" ? baseline : {};
  const out = {};
  for (const [day, metrics] of Object.entries(cur)) {
    const baseDay = base[day] || {};
    const next = {};
    for (const [key, value] of Object.entries(metrics || {})) {
      const currentValue = Number(value);
      if (!Number.isFinite(currentValue)) continue;
      const delta = currentValue - (Number(baseDay[key]) || 0);
      if (delta > 0) next[key] = delta;
    }
    if (Object.keys(next).length > 0) out[day] = next;
  }
  return out;
};

// State + setter come from the dashboard's single useStorage instance rather
// than a useStorage call here: a second instance would write the shared
// sessionStorage key with its own stale copy of the other slice, clobbering
// the watch history saved by useWatchHistory (and vice versa).
const useActivity = ({user, activity, setActivity}) => {
  const {fetchActivity} = useApi();
  const [dobEpoch, setDobEpoch] = useState(0);
  const replaceLocalRef = useRef(false);

  const activityRef = useRef(activity);

  useEffect(() => {
    activityRef.current = activity;
  }, [activity]);

  useEffect(() => {
    const customerId = user?.customer_id;
    if (!customerId) return undefined;
    const onResolved = (e) => {
      if (e?.detail?.customerId && e.detail.customerId !== customerId) return;
      replaceLocalRef.current = true;
      setDobEpoch((n) => n + 1);
    };
    window.addEventListener(DOB_RESOLVED_EVENT, onResolved);
    return () => window.removeEventListener(DOB_RESOLVED_EVENT, onResolved);
  }, [user?.customer_id]);

  useEffect(() => {
    if (!user?.customer_id && !activity) return undefined;

    let cancelled = false;
    const replaceLocal = replaceLocalRef.current;
    replaceLocalRef.current = false;
    const dob = readStoredDob(user?.customer_id);
    // Snapshot at fetch start: post-DOB clear is usually already {}, so only
    // ticks recorded while this request is in flight survive the merge.
    const baselineAtFetch = replaceLocal
      ? JSON.parse(JSON.stringify(activityRef.current || {}))
      : null;

    const applyRemote = (remote) => {
      if (cancelled) return;
      const remoteSafe = remote && typeof remote === "object" ? remote : {};
      setActivity((local) => {
        if (!replaceLocal) return mergeActivity(local, remoteSafe);
        const postDob = activitySinceBaseline(local, baselineAtFetch);
        return mergeActivity(remoteSafe, postDob);
      });
    };

    const load = (isRetry = false) =>
      fetchActivity(user.customer_id, dob)
        .then(applyRemote)
        .catch(() => {
          if (cancelled) return;
          // Failed fetch must not look like an authoritative empty record.
          // On DOB replace, retry once; otherwise leave local untouched.
          if (replaceLocal && !isRetry) return load(true);
        });

    load();
    return () => {
      cancelled = true;
    };
    // Keyed on the participant, not the user object: App replaces that object
    // for unrelated changes (e.g. the avatar URL), which refetched this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id, dobEpoch]);

  return {activityRef};
};
export default useActivity;
